import { useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { Button, Col, Row, Typography } from 'antd';

import CustomTable from 'pages/company-profile/components/tabs/contacts/components/CustomTable';

import {
  useGetQaSpecCertificates,
  useGetQaSpecContact,
  useGetQaSpecLocations,
  useMutationDeleteContactLinks,
  InvalidateQASQueries,
} from './queries';

import {
  getCertificateColumns,
  getContactColumns,
  getLocationColumns,
} from './hook';
import { useGetProductFullView } from 'hooks';

import { useContactColumns } from 'pages/company-profile/components/tabs/contacts/companyContact/hook';
import { useLocationColumns } from 'pages/company-profile/components/tabs/contacts/companyLocation/hook';
import { useCertificateColumns } from 'pages/company-profile/components/tabs/contacts/companyCertificate/hook';
import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';

import {
  CustomNotification,
  dialogFunction,
  FormAddButton,
  FormCancelButton,
  FormDeleteButton,
  PreviewableModal,
} from 'common/components';
import QASpecAssociateModal from './QASpecAssociateModal';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';

import './QaSpecContact.less';

const QaSpecContacts = ({ id }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState({
    contactIds: [],
    locationIds: [],
    certificateIds: [],
  });
  const [isEdit, setIsEdit] = useState(false);
  const [visible, setVisible] = useState(false);
  const [stateActions, setStateActions] = useState({
    visible: false,
    data: null,
  });

  const hiddenFields = ['department', 'associatedRetailers'];

  const { columns: contactColumns } = useContactColumns(hiddenFields);
  const { columns: locationColumns } = useLocationColumns();
  const { columns: certificateColumns } = useCertificateColumns(
    [],
    setStateActions
  );
  const { invalidateQasQueries } = InvalidateQASQueries(id);
  const mutation = useMutationDeleteContactLinks();
  const { id: productId } = useParams();
  const { productFull } = useGetProductFullView({ productId });
  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productFull?.productId,
    isEnabled: !!productFull?.productId,
  });
  const qaSpecStatus = qaSpecDataHeader?.status || undefined;

  const {
    contactList,
    isLoading: contactLoading,
    refetchQaSpecContact,
  } = useGetQaSpecContact({
    id,
    isEnabled: true,
  });

  const {
    locationList,
    isLoading: locationLoading,
    refetchQaSpecLocation,
  } = useGetQaSpecLocations({
    id,
    isEnabled: true,
  });

  const {
    certificateList,
    isLoading: certificateLoading,
    refetchQaSpecCertificate,
  } = useGetQaSpecCertificates({ id, isEnabled: true });
  const [tableVersion, setTableVersion] = useState(0);

  const handleDeleteContactLinks = () => {
    try {
      const params = {
        productId: id,
        ...selectedRowKeys,
      };
      mutation.mutate(params, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            CustomNotification.success('Delete successfully');
            invalidateQasQueries();
            refetchQaSpecContact();
            refetchQaSpecLocation();
            refetchQaSpecCertificate();
            setSelectedRowKeys({
              contactIds: [],
              locationIds: [],
              certificateIds: [],
            });
            setTableVersion((v) => v + 1);
          } else {
            CustomNotification.error(response?.message || 'Server Error!');
          }
        },
        onError: () => {
          CustomNotification.error('Server Error!');
        },
      });
    } catch (error) {
      CustomNotification.error('Server Error!');
    }
  };

  const onClickDelete = () => {
    dialogFunction({
      type: 'warn',
      content: `Are you sure you want to delete the selected item(s)?`,
      okText: 'OK',
      okButtonProps: {
        type: 'primary',
      },
      onOk: handleDeleteContactLinks,
    });
  };

  const handleUpdateSelectedRowKey = (key, value) => {
    setSelectedRowKeys((pre) => {
      return { ...pre, [key]: value };
    });
  };

  const mappingContactList = contactList?.map((item) => ({
    ...item,
    key: item.id,
  }));

  const mappingLocationList = locationList?.map((item) => ({
    ...item,
    key: item.id,
    type: Array.isArray(item?.type) ? item?.type?.join(', ') : null,
  }));

  const mappingCertificateList = certificateList?.map((item) => ({
    ...item,
    key: item.id,
  }));

  const { certificateIds, contactIds, locationIds } = selectedRowKeys || {};

  const disabledDeleteButton =
    certificateIds?.length === 0 &&
    locationIds?.length === 0 &&
    contactIds?.length === 0;

  const { data } = stateActions;

  const fileType = data?.fileExtension || data?.fileType;

  const pdfPreview =
    data?.pdfPreview ||
    data?.thumbSizeFull ||
    data?.thumbSize2500 ||
    data?.thumbSize1000 ||
    data?.thumbSize500;

  const { linkPreview, extensionFile } = checkShowPdfPreview(
    pdfPreview,
    data?.url,
    fileType
  );

  return (
    <Row className='qa-spec-contact__container'>
      <Col style={{ marginLeft: 'auto' }}>
        {!isEdit &&
          qaSpecStatus !== 'Submitted' &&
          qaSpecStatus !== 'Approved' && (
            <Button
              icon={<EditOutlined />}
              onClick={() => setIsEdit(true)}
              style={{ border: 'none' }}
            />
          )}
        {isEdit && (
          <>
            {/* <FormAddButton
              text='Raise Request'
              icon={<QuestionCircleOutlined />}
              onClick={() => setVisible(true)}
              disabled
              style={{ marginRight: 8 }}
            /> */}
            <FormAddButton
              text='Add'
              onClick={() => setVisible(true)}
              style={{ marginRight: 8 }}
            />
            <FormDeleteButton
              onClick={onClickDelete}
              style={{ marginRight: 8 }}
              disabled={disabledDeleteButton}
            />
            <FormCancelButton
              onClick={() => {
                setIsEdit(false);
              }}
            />
          </>
        )}
      </Col>
      <Col span={24}>
        <Row justify='space-between'>
          <Col span={4}>
            <div style={{ marginBottom: 12 }}>
              <TraceableCommentContainer
                qaSpecName={'Contacts'}
                qaSpecFieldName={'Company Contact'}
              >
                <QaSpecHelpContentLabel
                  fieldPath={'qaSpecification.contacts.companyContacts'}
                >
                  <Typography.Text strong>Company Contact</Typography.Text>
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            </div>
          </Col>
        </Row>
        <div style={{ height: 187 }}>
          <CustomTable
            key={`contact-${tableVersion}`}
            columns={contactColumns}
            loading={contactLoading}
            dataSource={mappingContactList || []}
            selectedRowKeys={selectedRowKeys.contactIds}
            setSelectedRowKeys={(value) => {
              handleUpdateSelectedRowKey('contactIds', value);
            }}
            isMultipleSelect
          />
        </div>

        <Row justify='space-between'>
          <Col span={4}>
            <div style={{ marginBottom: 12, marginTop: 12 }}>
              <TraceableCommentContainer
                qaSpecName={'Locations'}
                qaSpecFieldName={'Company Locations'}
              >
                <QaSpecHelpContentLabel
                  fieldPath={'qaSpecification.locations.companyLocations'}
                >
                  <Typography.Text strong>Company Locations</Typography.Text>
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            </div>
          </Col>
        </Row>
        <div style={{ height: 187 }}>
          <CustomTable
            key={`locations-${tableVersion}`}
            columns={locationColumns}
            loading={locationLoading}
            dataSource={mappingLocationList || []}
            selectedRowKeys={selectedRowKeys.locationIds}
            setSelectedRowKeys={(value) => {
              handleUpdateSelectedRowKey('locationIds', value);
            }}
            isMultipleSelect
          />
        </div>
        <Row justify='space-between'>
          <Col span={4}>
            <div style={{ marginBottom: 12, marginTop: 12 }}>
              <TraceableCommentContainer
                qaSpecName={'Certificates'}
                qaSpecFieldName={'Company Certificates'}
              >
                <QaSpecHelpContentLabel
                  fieldPath={'qaSpecification.certificates.companyCertificates'}
                >
                  <Typography.Text strong>Company Certificates</Typography.Text>
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            </div>
          </Col>
        </Row>
        <div style={{ height: 187 }}>
          <CustomTable
            key={`certificate-${tableVersion}`}
            loading={certificateLoading}
            columns={certificateColumns}
            dataSource={mappingCertificateList || []}
            selectedRowKeys={selectedRowKeys.certificateIds}
            setSelectedRowKeys={(value) => {
              handleUpdateSelectedRowKey('certificateIds', value);
            }}
            isMultipleSelect
          />
        </div>
      </Col>

      {visible && (
        <QASpecAssociateModal visible={visible} setVisible={setVisible} />
      )}
      {stateActions.visible && (
        <PreviewableModal
          id={data?.id}
          link={linkPreview}
          title={data?.certificateName}
          visible={stateActions.visible}
          fileName={data?.certificateName}
          cancelModal={() => setStateActions({ visible: false, data: null })}
          isDownload={false}
          fileType={extensionFile}
          assetData={data}
          isDocument={true}
        />
      )}
    </Row>
  );
};

export default QaSpecContacts;
